import React, { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "../../hooks/use-toast";
import { business } from "../../mock";
import { Phone, Building2, Instagram, Mail, MapPin } from "lucide-react";

const ContactForm = ({ source = "Website Contact Form" }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast({ title: "Please complete required fields", description: "Name and email are required." });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source }),
      });
      if (!res.ok) throw new Error("send failed");
      toast({ title: "Thank you for reaching out", description: "Omer & the Capital Corner Group team will be in touch shortly." });
      setForm({ firstName: "", lastName: "", phone: "", email: "", message: "", consent: false });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f3f1ec] py-24 md:py-32">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="text-center mb-16 reveal">
          <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-5">Contact</p>
          <h2 className="font-serif-display text-5xl md:text-7xl font-light text-[#1a1a1a]">Discover How We Can Help</h2>
          <p className="text-neutral-500 text-[15px] leading-relaxed max-w-2xl mx-auto mt-6">
            Looking to buy or sell your dream home? We&rsquo;re here to make the process simple and stress-free with expert advice and personalized support across {business.area}.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 reveal">
          {/* Contact info */}
          <div className="space-y-8">
            <a href={`tel:${business.contactPhone}`} className="flex items-start gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center border border-neutral-300 text-[#1a1a1a] group-hover:bg-[#1a1a1a] group-hover:text-white transition-colors"><Phone size={17} /></span>
              <span>
                <span className="block font-serif-display text-xl text-[#1a1a1a]">{business.contactPhone}</span>
                <span className="block text-[10px] tracking-luxe uppercase text-neutral-500 mt-1">Contact Number</span>
              </span>
            </a>
            <a href={`tel:${business.officePhone}`} className="flex items-start gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center border border-neutral-300 text-[#1a1a1a] group-hover:bg-[#1a1a1a] group-hover:text-white transition-colors"><Building2 size={17} /></span>
              <span>
                <span className="block font-serif-display text-xl text-[#1a1a1a]">{business.officePhone}</span>
                <span className="block text-[10px] tracking-luxe uppercase text-neutral-500 mt-1">Office Number</span>
              </span>
            </a>
            <a href={business.instagram} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center border border-neutral-300 text-[#1a1a1a] group-hover:bg-[#1a1a1a] group-hover:text-white transition-colors"><Instagram size={17} /></span>
              <span>
                <span className="block font-serif-display text-xl text-[#1a1a1a]">{business.instagramHandle}</span>
                <span className="block text-[10px] tracking-luxe uppercase text-neutral-500 mt-1">Follow on Instagram</span>
              </span>
            </a>
            <a href={`mailto:${business.email}`} className="flex items-start gap-4 group">
              <span className="w-11 h-11 flex items-center justify-center border border-neutral-300 text-[#1a1a1a] group-hover:bg-[#1a1a1a] group-hover:text-white transition-colors"><Mail size={17} /></span>
              <span>
                <span className="block font-serif-display text-xl text-[#1a1a1a] break-all">{business.email}</span>
                <span className="block text-[10px] tracking-luxe uppercase text-neutral-500 mt-1">Email Us</span>
              </span>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center border border-neutral-300 text-[#1a1a1a]"><MapPin size={17} /></span>
              <span>
                <span className="block font-serif-display text-lg text-[#1a1a1a] leading-snug">{business.address}</span>
                <span className="block text-[10px] tracking-luxe uppercase text-neutral-500 mt-1">Office</span>
              </span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-8 md:p-10 border border-black/[0.06] shadow-[0_30px_70px_rgba(0,0,0,0.06)]">
          <div>
            <label className="block text-[10px] tracking-luxe uppercase text-neutral-500 mb-2">First Name</label>
            <Input name="firstName" value={form.firstName} onChange={handle} className="bg-transparent border-0 border-b border-neutral-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#b4936a]" />
          </div>
          <div>
            <label className="block text-[10px] tracking-luxe uppercase text-neutral-500 mb-2">Last Name</label>
            <Input name="lastName" value={form.lastName} onChange={handle} className="bg-transparent border-0 border-b border-neutral-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#b4936a]" />
          </div>
          <div>
            <label className="block text-[10px] tracking-luxe uppercase text-neutral-500 mb-2">Phone</label>
            <Input name="phone" value={form.phone} onChange={handle} className="bg-transparent border-0 border-b border-neutral-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#b4936a]" />
          </div>
          <div>
            <label className="block text-[10px] tracking-luxe uppercase text-neutral-500 mb-2">Email</label>
            <Input name="email" type="email" value={form.email} onChange={handle} className="bg-transparent border-0 border-b border-neutral-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#b4936a]" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-luxe uppercase text-neutral-500 mb-2">Message</label>
            <Textarea name="message" value={form.message} onChange={handle} rows={4} className="bg-transparent border-0 border-b border-neutral-300 rounded-none px-0 resize-none focus-visible:ring-0 focus-visible:border-[#b4936a]" />
          </div>
          <p className="md:col-span-2 text-[11px] text-neutral-400 leading-relaxed">
            I agree to be contacted by Capital Corner Group via call, email, and text for real estate services. To opt out, you can reply 'stop' at any time. Message and data rates may apply.
          </p>
          <div className="md:col-span-2 mt-2">
            <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="bg-[#0d0d0d] text-white rounded-none px-12 py-6 text-[11px] tracking-luxe uppercase hover:bg-[#b4936a] transition-colors disabled:opacity-60">
              {loading ? "Sending…" : "Submit Request"}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
